import { useEffect, useState } from "react";
import api from "../services/api";
import { Button } from "../components/ui/button";
import { Gift, Copy, Check, Users, IndianRupee, Clock, Loader2, Share2, ExternalLink, MessageSquare, ShieldCheck, Trophy } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { toast } from "sonner";

export default function Referral() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.get("/referral/my")
      .then(({ data }) => setData(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const copyCode = () => {
    if (!data?.code) return;
    navigator.clipboard.writeText(data.code);
    setCopied(true);
    toast.success("Referral code copied!");
    setTimeout(() => setCopied(false), 2000);
  };

  const shareLink = () => {
    const url = `${window.location.origin}/register?ref=${data?.code}`;
    if (navigator.share) {
      navigator.share({ title: "Join PickNGo", text: `Use my referral code ${data?.code} and get INR ${data?.discount_amount} off your first order!`, url });
    } else {
      navigator.clipboard.writeText(url);
      toast.success("Referral link copied!");
    }
  };

  const shareWhatsApp = () => {
    const url = `${window.location.origin}/register?ref=${data?.code}`;
    const text = encodeURIComponent(`Hey! Join PickNGo for fast deliveries. Use my referral code *${data?.code}* and get INR ${data?.discount_amount} off your first order!\n${url}`);
    window.open(`https://wa.me/?text=${text}`, "_blank");
  };

  const shareSMS = () => {
    const url = `${window.location.origin}/register?ref=${data?.code}`;
    const text = `Join PickNGo for fast deliveries. Use my referral code ${data?.code} and get INR ${data?.discount_amount} off your first order. ${url}`;
    // sms: scheme — works on iOS/Android, no-op on desktop
    window.location.href = `sms:?&body=${encodeURIComponent(text)}`;
  };

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <Loader2 className="h-8 w-8 animate-spin text-[#FF6B00]" />
    </div>
  );

  return (
    <div className="space-y-6 max-w-4xl mx-auto" data-testid="referral-page">
      {/* Header */}
      <div className="animate-fadeInUp">
        <h1 className="font-['Outfit'] text-2xl sm:text-3xl font-bold text-slate-900">Refer & Earn</h1>
        <p className="text-sm text-slate-500 mt-1">Invite friends and earn INR {data?.reward_amount} for each successful referral</p>
      </div>

      {/* Referral Code Card */}
      <div className="bg-gradient-to-br from-[#FF6B00] to-orange-600 rounded-2xl p-6 md:p-8 text-white shadow-lg shadow-orange-500/20 animate-fadeInUp" data-testid="referral-code-card">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <p className="text-orange-100 text-sm font-medium mb-1">Your Referral Code</p>
            <div className="flex items-center gap-3">
              <span className="font-mono text-3xl sm:text-4xl font-extrabold tracking-[0.15em]" data-testid="referral-code-display">{data?.code}</span>
              <button
                onClick={copyCode}
                className="w-10 h-10 bg-white/20 hover:bg-white/30 rounded-xl flex items-center justify-center transition-all active:scale-95"
                data-testid="copy-code-btn"
              >
                {copied ? <Check className="h-5 w-5" /> : <Copy className="h-5 w-5" />}
              </button>
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={shareLink} className="bg-white/20 hover:bg-white/30 text-white border-0 rounded-xl gap-2" data-testid="share-link-btn">
              <Share2 className="h-4 w-4" /> Share Link
            </Button>
            <Button onClick={shareWhatsApp} className="bg-green-500 hover:bg-green-600 text-white border-0 rounded-xl gap-2" data-testid="share-whatsapp-btn">
              <ExternalLink className="h-4 w-4" /> WhatsApp
            </Button>
            <Button onClick={shareSMS} className="bg-blue-500 hover:bg-blue-600 text-white border-0 rounded-xl gap-2" data-testid="share-sms-btn">
              <MessageSquare className="h-4 w-4" /> SMS
            </Button>
          </div>
        </div>
        <div className="mt-5 pt-5 border-t border-white/20 grid grid-cols-2 gap-4">
          <div>
            <p className="text-orange-100 text-xs font-medium">You Get</p>
            <p className="text-lg font-bold mt-0.5">INR {data?.reward_amount} wallet credit</p>
            <p className="text-orange-200 text-xs mt-0.5">When they complete first order</p>
          </div>
          <div>
            <p className="text-orange-100 text-xs font-medium">They Get</p>
            <p className="text-lg font-bold mt-0.5">INR {data?.discount_amount} off</p>
            <p className="text-orange-200 text-xs mt-0.5">On their first order</p>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5 card-hover animate-fadeInUp" data-testid="stat-total">
          <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center mb-3">
            <Users className="h-5 w-5 text-blue-600" />
          </div>
          <p className="font-['Outfit'] text-2xl font-bold text-slate-900">{data?.total_referrals ?? 0}</p>
          <p className="text-sm text-slate-500 mt-0.5">Total Referrals</p>
        </div>
        <div className="bg-white rounded-2xl shadow-sm border border-emerald-100 p-5 card-hover animate-fadeInUp stagger-1" data-testid="stat-completed">
          <div className="w-10 h-10 bg-emerald-50 rounded-xl flex items-center justify-center mb-3">
            <Check className="h-5 w-5 text-emerald-600" />
          </div>
          <p className="font-['Outfit'] text-2xl font-bold text-emerald-700">{data?.completed ?? 0}</p>
          <p className="text-sm text-slate-500 mt-0.5">Completed</p>
        </div>
        <div className="bg-white rounded-2xl shadow-sm border border-amber-100 p-5 card-hover animate-fadeInUp stagger-2" data-testid="stat-pending">
          <div className="w-10 h-10 bg-amber-50 rounded-xl flex items-center justify-center mb-3">
            <Clock className="h-5 w-5 text-amber-600" />
          </div>
          <p className="font-['Outfit'] text-2xl font-bold text-amber-700">{data?.pending ?? 0}</p>
          <p className="text-sm text-slate-500 mt-0.5">Pending</p>
        </div>
        <div className="bg-white rounded-2xl shadow-sm border border-green-100 p-5 card-hover animate-fadeInUp stagger-3" data-testid="stat-earned">
          <div className="w-10 h-10 bg-green-50 rounded-xl flex items-center justify-center mb-3">
            <IndianRupee className="h-5 w-5 text-green-600" />
          </div>
          <p className="font-['Outfit'] text-2xl font-bold text-green-700">INR {data?.total_earned ?? 0}</p>
          <p className="text-sm text-slate-500 mt-0.5">Total Earned</p>
        </div>
      </div>

      {/* Fair-use notice + Leaderboard CTA */}
      <div className="grid sm:grid-cols-2 gap-4 animate-fadeInUp">
        <div className="bg-white rounded-2xl border border-slate-100 p-4 flex gap-3" data-testid="fair-use-card">
          <div className="w-10 h-10 bg-emerald-50 rounded-xl flex items-center justify-center shrink-0">
            <ShieldCheck className="h-5 w-5 text-emerald-600" />
          </div>
          <div>
            <p className="text-sm font-semibold text-slate-900">Fair-use limit</p>
            <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">
              Max <b>5 successful referrals / 30 days</b> per account. Same-IP duplicates are auto-blocked to keep things fair.
            </p>
          </div>
        </div>
        <a href="/dashboard/leaderboard" className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl border border-amber-100 p-4 flex gap-3 hover:shadow-md transition-shadow" data-testid="leaderboard-cta">
          <div className="w-10 h-10 bg-amber-500 rounded-xl flex items-center justify-center shrink-0">
            <Trophy className="h-5 w-5 text-white" />
          </div>
          <div>
            <p className="text-sm font-semibold text-slate-900">Monthly Leaderboard</p>
            <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">
              Top 3 referrers win bonuses (INR 200 / 100 / 50). View rankings →
            </p>
          </div>
        </a>
      </div>

      {/* How it Works */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5 md:p-6 animate-fadeInUp" data-testid="how-it-works">        <h2 className="font-['Outfit'] text-base font-semibold text-slate-900 mb-4">How It Works</h2>
        <div className="grid sm:grid-cols-3 gap-4">
          {[
            { step: "1", title: "Share Your Code", desc: "Send your unique referral code to friends via WhatsApp, SMS, or any channel", color: "bg-blue-500" },
            { step: "2", title: "They Sign Up", desc: "Your friend registers on PickNGo using your referral code", color: "bg-[#FF6B00]" },
            { step: "3", title: "Both Get Rewarded", desc: `They get INR ${data?.discount_amount} off their first order. You get INR ${data?.reward_amount} wallet credit when it's delivered!`, color: "bg-emerald-500" },
          ].map((s) => (
            <div key={s.step} className="flex gap-3">
              <div className={`w-8 h-8 ${s.color} rounded-lg flex items-center justify-center shrink-0`}>
                <span className="text-white text-sm font-bold">{s.step}</span>
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-900">{s.title}</p>
                <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">{s.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Referral History */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 animate-fadeInUp" data-testid="referral-history">
        <div className="p-5 border-b border-slate-100">
          <h2 className="font-['Outfit'] text-base font-semibold text-slate-900">Referral History</h2>
        </div>
        {!data?.referrals?.length ? (
          <div className="p-12 text-center">
            <Gift className="h-10 w-10 text-slate-300 mx-auto mb-3" />
            <p className="text-base font-semibold text-slate-700">No referrals yet</p>
            <p className="text-sm text-slate-400 mt-1">Share your code to start earning!</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-slate-100 bg-slate-50/60">
                  <TableHead className="text-[10px] font-bold uppercase tracking-widest text-slate-400 pl-5">Friend</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Status</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Reward</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.referrals.map((r, i) => (
                  <TableRow key={r.referred_id || i} className="border-slate-50">
                    <TableCell className="pl-5">
                      <p className="text-sm font-semibold text-slate-900">{r.referred_name || "—"}</p>
                    </TableCell>
                    <TableCell>
                      {r.status === "completed" ? (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg border text-[11px] font-semibold bg-emerald-50 text-emerald-700 border-emerald-200">
                          <Check className="h-3 w-3" /> Completed
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg border text-[11px] font-semibold bg-amber-50 text-amber-700 border-amber-200">
                          <Clock className="h-3 w-3" /> Pending
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      {r.status === "completed" ? (
                        <span className="text-sm font-bold text-green-600">+INR {data.reward_amount}</span>
                      ) : (
                        <span className="text-xs text-slate-400">Awaiting first order</span>
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-slate-500">{new Date(r.created_at).toLocaleDateString()}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
}
